import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';

/**
 * Enum for defining different variants of the price component.
 * This enum is used to specify the visual style of the price component.
 */
var PriceVariant;
(function (PriceVariant) {
    /**
     * Represents the stacked variant of the component.
     * Use this variant for displaying components in a stacked layout.
     */
    PriceVariant["Stacked"] = "stacked";
    /**
     * Represents the default variant of the component.
     * Use this variant for the standard layout of the component.
     */
    PriceVariant["Default"] = "default";
    // Add new variants here if needed in the future
})(PriceVariant || (PriceVariant = {}));

const gPriceCss = ".g__price{font-family:\"Superscripts-Roboto\", \"Roboto\", \"Noto Sans TC\", \"Noto Sans SC\", \"Noto Sans JP\", \"Noto Sans KR\", \"Prompt\", sans-serif}.g__price{display:flex;flex-wrap:wrap;box-sizing:border-box;font-size:1rem}.g__price--stacked{flex-direction:column;font-size:2rem;font-weight:700;line-height:2.5rem}.g__price .from-text{display:inline-block;text-decoration:none;margin-right:0.25rem}.g__price .and-up-text{margin-left:0.25rem}.g__price .savings-text{display:inline-flex;font-size:1rem;margin-left:0.5rem;font-weight:400;line-height:1.5}.g__price .details-text{font-size:1rem;margin-left:0.5rem;font-weight:400}.g__price__discounted-price{font-weight:700;display:flex;align-items:baseline;white-space:nowrap}.g__price__original-price{display:inline-flex;font-weight:700;margin-right:0.6875rem;white-space:nowrap}.g__price--strike{font-weight:400;text-decoration:line-through;display:inline-block;font-size:1rem}.g__price--blue{color:#106fad}";
const GPriceStyle0 = gPriceCss;

const GPrice$1 = /*@__PURE__*/ proxyCustomElement(class GPrice extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.originalPrice = undefined;
        this.discountedPrice = undefined;
        this.blueDiscountFont = false;
        this.hasText = false;
        this.fromText = undefined;
        this.andUpText = undefined;
        this.variant = PriceVariant.Default;
        this.savingsText = undefined;
        this.detailsText = undefined;
        this.dataTestId = undefined;
    }
    get componentClasses() {
        return {
            g__price: true,
            'g__price--stacked': this.variant === PriceVariant.Stacked,
        };
    }
    get originalPriceClasses() {
        return {
            'g__price__original-price': true,
            'g__price--strike': !!this.discountedPrice,
        };
    }
    get discountPriceClasses() {
        return {
            'g__price__discounted-price': true,
            'g__price--blue': this.variant === PriceVariant.Default && this.blueDiscountFont,
        };
    }
    renderOriginalPrice() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (!this.originalPrice)
            return null;
        return (h("div", { class: this.originalPriceClasses, "data-testid": (_b = (_a = this.dataTestId) === null || _a === void 0 ? void 0 : _a.originalPrice) === null || _b === void 0 ? void 0 : _b.wrapper }, this.variant === PriceVariant.Default && this.hasText && this.fromText && (h("span", { class: "from-text", "data-testid": (_d = (_c = this.dataTestId) === null || _c === void 0 ? void 0 : _c.originalPrice) === null || _d === void 0 ? void 0 : _d.fromText, innerHTML: this.fromText })), h("span", { class: "original-price-value", "data-testid": (_f = (_e = this.dataTestId) === null || _e === void 0 ? void 0 : _e.originalPrice) === null || _f === void 0 ? void 0 : _f.value, innerHTML: this.originalPrice }), this.variant === PriceVariant.Default && this.hasText && this.andUpText && !this.discountedPrice && (h("span", { class: "and-up-text", "data-testid": (_g = this.dataTestId) === null || _g === void 0 ? void 0 : _g.andUpText, innerHTML: this.andUpText })), this.variant === PriceVariant.Stacked && this.discountedPrice && this.savingsText && (h("span", { class: "savings-text", "data-testid": (_j = (_h = this.dataTestId) === null || _h === void 0 ? void 0 : _h.originalPrice) === null || _j === void 0 ? void 0 : _j.savingsText, innerHTML: this.savingsText })), this.variant === PriceVariant.Stacked && !this.discountedPrice && this.detailsText && (h("span", { class: "details-text", "data-testid": (_k = this.dataTestId) === null || _k === void 0 ? void 0 : _k.detailsText, innerHTML: this.detailsText }))));
    }
    renderDiscountedPrice() {
        var _a, _b, _c, _d;
        if (!this.originalPrice || !this.discountedPrice)
            return null;
        return (h("div", { class: this.discountPriceClasses, "data-testid": (_b = (_a = this.dataTestId) === null || _a === void 0 ? void 0 : _a.discountedPrice) === null || _b === void 0 ? void 0 : _b.wrapper, innerHTML: this.discountedPrice }, this.variant === PriceVariant.Stacked && this.detailsText && (h("span", { class: "details-text", "data-testid": (_c = this.dataTestId) === null || _c === void 0 ? void 0 : _c.detailsText, innerHTML: this.detailsText })), this.variant === PriceVariant.Default && this.hasText && this.andUpText && (h("span", { class: "and-up-text", "data-testid": (_d = this.dataTestId) === null || _d === void 0 ? void 0 : _d.andUpText, innerHTML: this.andUpText }))));
    }
    render() {
        var _a;
        return (h(Host, { key: 'a0ba710015b365a3a9d1a0357fd61df2297d8fc0', class: this.componentClasses, "data-testid": (_a = this.dataTestId) === null || _a === void 0 ? void 0 : _a.host }, this.renderOriginalPrice(), this.renderDiscountedPrice()));
    }
    static get style() { return GPriceStyle0; }
}, [0, "g-price", {
        "originalPrice": [1, "original-price"],
        "discountedPrice": [1, "discounted-price"],
        "blueDiscountFont": [4, "blue-discount-font"],
        "hasText": [4, "has-text"],
        "fromText": [1, "from-text"],
        "andUpText": [1, "and-up-text"],
        "variant": [1],
        "savingsText": [1, "savings-text"],
        "detailsText": [1, "details-text"],
        "dataTestId": [1040]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["g-price"];
    components.forEach(tagName => { switch (tagName) {
        case "g-price":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, GPrice$1);
            }
            break;
    } });
}

const GPrice = GPrice$1;
const defineCustomElement = defineCustomElement$1;

export { GPrice, defineCustomElement };

//# sourceMappingURL=g-price.js.map